import React from 'react';
import { Button } from 'react-bootstrap';
import { Trash2, Sliders, Zap } from 'lucide-react';
import { SPECIFICATION_PRESETS } from '../../../../helper/bom';

const TechnicalSpecMatrix = ({
  assemblies,
  setAssemblies
}) => {

  const applyPreset = (aIdx, presetKey) => {
    const presetData = SPECIFICATION_PRESETS[presetKey];
    if (!presetData) return;

    setAssemblies(prev => prev.map((ass, idx) => {
      if (idx !== aIdx) return ass;
      return {
        ...ass,
        specifications: JSON.parse(JSON.stringify(presetData))
      };
    }));
  };

  const clearSpecs = (aIdx) => {
    setAssemblies(prev => prev.map((ass, idx) => {
      if (idx !== aIdx) return ass;
      return { ...ass, specifications: [] };
    }));
  };

  const addSpecGroup = (aIdx) => {
    setAssemblies(prev => prev.map((ass, idx) => {
      if (idx !== aIdx) return ass;
      const specList = ass.specifications || [];
      const newGroup = {
        group_code: `S${specList.length + 1}`,
        group_title: "Custom Parameter Group",
        parameters: [
          { parameter_name: "", details: "", type_value: "", unit: "" }
        ]
      };
      return {
        ...ass,
        specifications: [...specList, newGroup]
      };
    }));
  };

  const removeSpecGroup = (aIdx, gIdx) => {
    setAssemblies(prev => prev.map((ass, idx) => {
      if (idx !== aIdx) return ass;
      return {
        ...ass,
        specifications: (ass.specifications || []).filter((_, i) => i !== gIdx)
      };
    }));
  };

  const updateGroupTitle = (aIdx, gIdx, field, val) => {
    setAssemblies(prev => prev.map((ass, idx) => {
      if (idx !== aIdx) return ass;
      const updatedSpecs = (ass.specifications || []).map((grp, i) => {
        if (i !== gIdx) return grp;
        return { ...grp, [field]: val };
      });
      return {
        ...ass,
        specifications: updatedSpecs
      };
    }));
  };

  const addParam = (aIdx, gIdx) => {
    setAssemblies(prev => prev.map((ass, idx) => {
      if (idx !== aIdx) return ass;
      const updatedSpecs = (ass.specifications || []).map((grp, i) => {
        if (i !== gIdx) return grp;
        const currentParams = grp.parameters || [];
        const newParam = { parameter_name: "", details: "", type_value: "", unit: "" };
        return {
          ...grp,
          parameters: [...currentParams, newParam]
        };
      });
      return {
        ...ass,
        specifications: updatedSpecs
      };
    }));
  };

  const removeParam = (aIdx, gIdx, pIdx) => {
    setAssemblies(prev => prev.map((ass, idx) => {
      if (idx !== aIdx) return ass;
      const updatedSpecs = (ass.specifications || []).map((grp, i) => {
        if (i !== gIdx) return grp;
        return {
          ...grp,
          parameters: (grp.parameters || []).filter((_, pI) => pI !== pIdx)
        };
      });
      return {
        ...ass,
        specifications: updatedSpecs
      };
    }));
  };

  const updateParam = (aIdx, gIdx, pIdx, field, val) => {
    setAssemblies(prev => prev.map((ass, idx) => {
      if (idx !== aIdx) return ass;
      const updatedSpecs = (ass.specifications || []).map((grp, i) => {
        if (i !== gIdx) return grp;
        const updatedParams = (grp.parameters || []).map((param, pI) => {
          if (pI !== pIdx) return param;
          return { ...param, [field]: val };
        });
        return {
          ...grp,
          parameters: updatedParams
        };
      });
      return {
        ...ass,
        specifications: updatedSpecs
      };
    }));
  };

  return (
    <div className="bom-spec-matrix">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h6 className="mb-0 fw-bold d-flex align-items-center gap-2" style={{ color: "#1e293b", fontSize: "14px" }}>
            <Sliders size={16} className="text-primary" />
            Technical & Engineering Specification Matrices
          </h6>
          <small className="text-muted" style={{ fontSize: "12px" }}>
            Define engineering technical parameters per panel (Click presets to load standard specs)
          </small>
        </div>
      </div>

      {assemblies.length === 0 ? (
        <div className="text-center py-4 border rounded bg-white text-muted small" style={{ fontSize: "12px" }}>
          Please add assemblies in Step 2 first.
        </div>
      ) : (
        assemblies.map((assembly, aIdx) => (
          <div key={aIdx} className="border rounded-3 mb-4 bg-white shadow-sm overflow-hidden" style={{ borderColor: "#cbd5e1" }}>
            <div className="d-flex justify-content-between align-items-center bg-light p-2 px-3 border-bottom flex-wrap gap-2">
              <div>
                <strong className="text-primary" style={{ fontSize: "13px" }}>
                  #{aIdx + 1}. {assembly.assembly_name || 'Assembly'}
                </strong>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted small" style={{ fontSize: "11.5px" }}>Presets:</span>
                <Button
                  variant="outline-primary"
                  size="sm"
                  className="py-1 px-2 fw-semibold"
                  style={{ fontSize: "11.5px" }}
                  onClick={() => applyPreset(aIdx, 'ELECTRICAL_PANEL')}
                >
                  <Zap size={12} className="me-1" /> Electrical Panel (AA-AG)
                </Button>
                <Button
                  variant="outline-info"
                  size="sm"
                  className="py-1 px-2 fw-semibold"
                  style={{ fontSize: "11.5px" }}
                  onClick={() => applyPreset(aIdx, 'STRUCTURAL_FABRICATION')}
                >
                  <Zap size={12} className="me-1" /> Structural / Fab
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  className="py-1 px-2"
                  style={{ fontSize: "11.5px" }}
                  onClick={() => addSpecGroup(aIdx)}
                >
                  + Add Group
                </Button>
                {assembly.specifications && assembly.specifications.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm p-1 border-0"
                    onClick={() => clearSpecs(aIdx)}
                    title="Clear specifications"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            </div>

            <div className="p-3 bg-light">
              {(!assembly.specifications || assembly.specifications.length === 0) ? (
                <div className="text-center py-3 text-muted small bg-white rounded border" style={{ fontSize: "12px" }}>
                  No specifications loaded. Click <strong>"⚡ Electrical Panel (AA-AG)"</strong> or <strong>"+ Add Group"</strong> above.
                </div>
              ) : (
                assembly.specifications.map((group, gIdx) => (
                  <div key={gIdx} className="border rounded bg-white p-2 mb-3 shadow-sm" style={{ borderColor: "#e2e8f0" }}>
                    <div className="d-flex justify-content-between align-items-center mb-2 pb-1 border-bottom">
                      <div className="d-flex align-items-center gap-2">
                        <input
                          type="text"
                          className="form-control form-control-sm text-center fw-bold py-1"
                          style={{ width: "50px", height: "28px", fontSize: "12px" }}
                          value={group.group_code}
                          onChange={(e) => updateGroupTitle(aIdx, gIdx, 'group_code', e.target.value)}
                        />
                        <input
                          type="text"
                          className="form-control form-control-sm fw-bold py-1"
                          style={{ width: "280px", height: "28px", fontSize: "12.5px" }}
                          value={group.group_title}
                          onChange={(e) => updateGroupTitle(aIdx, gIdx, 'group_title', e.target.value)}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm p-1 border-0"
                        onClick={() => removeSpecGroup(aIdx, gIdx)}
                        title="Delete Group"
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>

                    <table className="table table-sm table-bordered mb-2 align-middle bg-white" style={{ fontSize: "12px" }}>
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "4%", padding: "4px 8px" }} className="text-center">#</th>
                          <th style={{ width: "36%", padding: "4px 8px" }}>Parameter Name</th>
                          <th style={{ width: "30%", padding: "4px 8px" }}>Standard / Details</th>
                          <th style={{ width: "26%", padding: "4px 8px" }}>Specified Value</th>
                          <th style={{ width: "4%", padding: "4px 8px" }} className="text-center"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(group.parameters || []).map((param, pIdx) => (
                          <tr key={pIdx}>
                            <td className="text-center text-muted fw-bold py-1" style={{ fontSize: "11px" }}>{pIdx + 1}</td>
                            <td className="py-1 px-2">
                              <input
                                type="text"
                                className="form-control form-control-sm py-1"
                                style={{ height: "28px", fontSize: "12px" }}
                                value={param.parameter_name}
                                onChange={(e) => updateParam(aIdx, gIdx, pIdx, 'parameter_name', e.target.value)}
                              />
                            </td>
                            <td className="py-1 px-2">
                              <input
                                type="text"
                                className="form-control form-control-sm py-1"
                                style={{ height: "28px", fontSize: "12px" }}
                                value={param.details}
                                onChange={(e) => updateParam(aIdx, gIdx, pIdx, 'details', e.target.value)}
                              />
                            </td>
                            <td className="py-1 px-2">
                              <input
                                type="text"
                                className="form-control form-control-sm py-1 fw-semibold"
                                style={{ height: "28px", fontSize: "12px" }}
                                value={param.type_value}
                                onChange={(e) => updateParam(aIdx, gIdx, pIdx, 'type_value', e.target.value)}
                              />
                            </td>
                            <td className="py-1 px-1 text-center">
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm p-1 border-0"
                                onClick={() => removeParam(aIdx, gIdx, pIdx)}
                              >
                                <Trash2 size={12} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <div className="text-end">
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 text-decoration-none fw-semibold text-primary"
                        style={{ fontSize: "11.5px" }}
                        onClick={() => addParam(aIdx, gIdx)}
                      >
                        + Add Parameter Row
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default TechnicalSpecMatrix;
